import I18n from 'I18n';
import FormattedMessage from 'I18n/components/FormattedMessage';
import { Text } from 'foundations-components/components/Text';
import { View } from 'foundations-components/components/View';
import { ScrollContainer } from 'foundations-components/transitional/components/ScrollContainer';
import { createToObject } from 'pricing-pages-lib/utils/queryParamUtils';
import { useRef } from 'react';
import RouterSideNav from 'ui-addon-react-router-dom/RouterSideNav';
import RouterSideNavItem from 'ui-addon-react-router-dom/RouterSideNavItem';
import { STANDALONE_CORE_SEATS } from '../../../constants/ReleaseInsights';
import { NavItemEnum } from '../../../constants/routing/NavItems';
import { useAddOnsPage } from '../../../hooks/useAddOnPage';
import { useHasReleaseInsight } from '../../../hooks/useBulkPurchaseOptions';
import { useCommerceHub } from '../../../hooks/useCommerceHub';
import { usePageParams } from '../../../hooks/usePageParams';
import { useShouldHideOpsOrSuitePage } from '../../../hooks/useShouldHideOpsOrSuitePage';
import { useWindowSize } from '../../../hooks/useWindowSize';
import { useAppSelector } from '../../../reducers';
import { isAppAuthSelector } from '../../../selectors/AuthSelectors';
import { isUngatedForBreezeIntelligenceSelector, isUngatedForHubSpotCreditsSelector, isUngatedForMarketingPlusSelector } from '../../../selectors/GatingSelectors';
import { segmentedNavItemsSelector } from '../../../selectors/RoutingSelectors';
import { useIsFreePortal } from '../../../v2/hooks/useProductInventory';
import CurrencyPicker from '../../common/CurrencyPicker';
import { Inbound25CopyChange } from '../../common/i18n/CopyChangeComponents';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
const SideNavWrapper = ({
  children
}) => {
  var _ref$current;
  const ref = useRef(null);
  const sideNavDomRect = (_ref$current = ref.current) === null || _ref$current === void 0 ? void 0 : _ref$current.getBoundingClientRect();
  // calculates offset of the side nav's bottom from the top of the viewport, accounts for navbar
  const sideNavTop = (sideNavDomRect === null || sideNavDomRect === void 0 ? void 0 : sideNavDomRect.top) || 0;
  const {
    height
  } = useWindowSize();
  const isAppAuth = useAppSelector(isAppAuthSelector);
  return /*#__PURE__*/_jsx(View, {
    position: isAppAuth ? 'fixed' : 'sticky',
    insetBlockStart: "24px",
    children: /*#__PURE__*/_jsx(ScrollContainer, {
      id: "scroll-container",
      scrollDirection: "vertical"
      // -48 to account for the insetBlockStart=24px of the the two Views
      ,
      maxHeight: `${height - (sideNavTop - 48)}px`,
      overflowY: "auto",
      children: /*#__PURE__*/_jsx(View, {
        marginInlineEnd: "900",
        marginBlockEnd: "1300",
        blockSize: "100%",
        paddingInlineStart: "200",
        children: /*#__PURE__*/_jsx(View, {
          insetBlockStart: "24px",
          children: /*#__PURE__*/_jsx("div", {
            ref: ref,
            children: children
          })
        })
      })
    })
  });
};
const SideNav = ({
  selectedNavItem
}) => {
  const {
    tab
  } = usePageParams();
  const isUngatedToMarketingPlus = useAppSelector(isUngatedForMarketingPlusSelector);
  const shouldHideOpsOrSuitePage = useShouldHideOpsOrSuitePage();
  const {
    hasReleaseInsight: isSmartCrm,
    loading: isLoadingSmartCrm
  } = useHasReleaseInsight({
    releaseInsight: STANDALONE_CORE_SEATS
  });
  const isUngatedForBreezeIntelligence = useAppSelector(isUngatedForBreezeIntelligenceSelector);
  const isUngatedForHubSpotCredits = useAppSelector(isUngatedForHubSpotCreditsSelector);
  const {
    showAddonsPage
  } = useAddOnsPage();
  const {
    showCommerceHubProductExperience,
    showCommerceHubPaymentsExperience
  } = useCommerceHub();
  const isAppAuth = useAppSelector(isAppAuthSelector);
  const {
    isFreePortal,
    loading: isInventoryLoading
  } = useIsFreePortal();
  const segmentedNavItems = useAppSelector(segmentedNavItemsSelector);
  const shouldHideNavSection = section => {
    return section === 'enhancements' && !showAddonsPage && !isUngatedForBreezeIntelligence && !isUngatedForHubSpotCredits;
  };
  const shouldHideNavItem = item => {
    return item === NavItemEnum.MARKETING_PLUS && !isUngatedToMarketingPlus || item === NavItemEnum.ADD_ONS && !showAddonsPage || item === NavItemEnum.BREEZE_INTELLIGENCE && !isUngatedForBreezeIntelligence || item === NavItemEnum.HUBSPOT_CREDITS && !isUngatedForHubSpotCredits ||
    // don't show free link if in-app pricing page and paid
    item === NavItemEnum.FREE && isAppAuth && !isFreePortal && !isInventoryLoading ||
    // don't show bundle link if in-app pricing page
    item === NavItemEnum.BUNDLE && isAppAuth ||
    // don't show recommended link if not in SSM0010 variant
    item === NavItemEnum.RECOMMENDED ||
    //don't show smart crm link if not ungated
    item === NavItemEnum.SMART_CRM && (isLoadingSmartCrm || !isSmartCrm) ||
    // hide commerce hub if neither experience is enabled
    item === NavItemEnum.COMMERCE && !showCommerceHubProductExperience && !showCommerceHubPaymentsExperience ||
    // hide for operations hub and suite for pre-seats
    (item === NavItemEnum.OPERATIONS || item === NavItemEnum.DATA || item === NavItemEnum.SUITE) && shouldHideOpsOrSuitePage;
  };
  const hasTabs = navItem => {
    return NavItemEnum.BUNDLE !== navItem && NavItemEnum.FREE !== navItem && NavItemEnum.COMMERCE !== navItem && NavItemEnum.MARKETING_PLUS !== navItem && NavItemEnum.ADD_ONS !== navItem && NavItemEnum.BREEZE_INTELLIGENCE !== navItem && NavItemEnum.HUBSPOT_CREDITS !== navItem && NavItemEnum.RECOMMENDED !== navItem;
  };
  const getPath = navItem => {
    const path = `/${navItem}`;
    return hasTabs(navItem) && tab ? `${path}/${tab}` : path;
  };
  return /*#__PURE__*/_jsxs(SideNavWrapper, {
    children: [Object.keys(segmentedNavItems).map((navSection, index) => {
      if (shouldHideNavSection(navSection)) return null;
      return /*#__PURE__*/_jsx(RouterSideNav, {
        divider: index !== 0,
        headingText: I18n.text(`pricingPage.sidenav.sections.${navSection}`),
        children: segmentedNavItems[navSection].map(navItem => {
          if (shouldHideNavItem(navItem)) return null;
          return /*#__PURE__*/_jsx(RouterSideNavItem, {
            "data-test-id": `side-nav-${navItem}`,
            "data-is-active": selectedNavItem === navItem,
            "data-selenium-class": "side-nav",
            active: selectedNavItem === navItem,
            title: /*#__PURE__*/_jsx(Text, {
              textWrap: "nowrap",
              children: /*#__PURE__*/_jsx(Inbound25CopyChange, {
                children: /*#__PURE__*/_jsx(FormattedMessage, {
                  message: `pricingPage.pages.products.tabs.${navItem}.tabTitle`
                })
              })
            }),
            to: createToObject(getPath(navItem)),
            padding: "400",
            marginInlineStart: "300"
          }, navItem);
        })
      }, navSection);
    }), /*#__PURE__*/_jsx(CurrencyPicker, {})]
  });
};
export default SideNav;